import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { colorsMapping, VariantColor } from '@repo/toolkit/colors'
import { handleKeyDown } from '@repo/toolkit/handle-key-down'
import { CustomOption, Option, OptionType } from '@repo/toolkit/types'
import useDropdownPosition from '@repo/toolkit/use-drop-down-position'
import { useKeydown } from '@repo/toolkit/use-keydown'
import { useEffect, useRef, useState } from 'react'
import { useHover, useOnClickOutside } from 'usehooks-ts'

import useTranslate from '../../../../apps/backoffice/src/lib/hooks/use-translate'
import { ChevronDownIcon, XIcon } from '../../icons'
import Checkbox from './checkbox'
import InputWrapper, { InputWrapperProps } from './input/input-wrapper'
import Typography, { typography } from './typography'

export type MultiSelectInputProps<T extends OptionType> = {
  /**
   * Options for the select input
   *
   * @note If the options are not provided, the input will be a manual input
   */
  options?: Option<T>[]

  /**
   * Default selected options
   */
  selectedOptions?: Option<T>[]

  /**
   * If true, the select will have a search input for filtering the options
   *
   * @note This option is useless if the options are not provided and won't be displayed
   */
  searchable?: boolean

  /**
   * If true, the input can accept free text
   */
  allowNewOption?: boolean

  /**
   * readOnly state of the input
   * @default false
   * */
  readOnly?: boolean

  /**
   * If true, the select will allow new options to be added
   * @default false
   */
  allowCustomOptions?: boolean

  /**
   * Indicate wether the dropdown should show normal under the component or above of everything
   */
  dropDownPosition?: 'under' | 'above'

  /**
   * Callback to handle the input change
   */
  onChange?: (value: T[]) => void

  /**
   * Callback to handle the input text change
   */
  onTextChange?: (text: string) => void

  /**
   * Variant for the pill color
   */
  variant: VariantColor

  type?: 'text' | 'number' | 'email'

  allowDecimals?: boolean

  onBlur?: () => void
} & Omit<
  InputWrapperProps,
  | 'children'
  | 'className'
  | 'icon'
  | 'iconStyle'
  | 'iconToRight'
  | 'active'
  | 'iconToTheRight'
  | 'focused'
>

const ENGLISH_ALPHABET_REGEX = /^[a-zA-ZÀ-ÿ\s]$/
const MultiSelectInput = <T extends OptionType>({
  options,
  selectedOptions,
  searchable,
  allowNewOption,
  variant = 'primary',
  type = 'text',
  allowCustomOptions = false,
  dropDownPosition = 'under',
  allowDecimals = true,
  onTextChange,
  onChange,
  onBlur,
  ...props
}: MultiSelectInputProps<T>) => {
  const [isOpen, setIsOpen] = useState(false)
  const [selected, setSelected] = useState<Option<T>[] | null>(null)
  const [availableOptions, setAvailableOptions] = useState<Option<T>[]>(options ?? [])
  const [inputValue, setInputValue] = useState<string>('')
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1)
  const [tempValue, setTempValue] = useState<string>('')
  const [customOptions, setCustomOptions] = useState<CustomOption<T>[]>([])

  const searchRef = useRef<HTMLInputElement>(null)
  const ref = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const toggle = () => setIsOpen((prev) => !prev)
  const { t } = useTranslate()
  const isHovered = useHover(ref)
  const isManualInput = options === undefined
  const isDisabled = Boolean(props.disabled || props.readOnly)

  // Use the custom hook to calculate dropdown dimensions when needed
  const dropdownDimensions = useDropdownPosition(ref, isOpen, dropDownPosition)

  const showCustomOption =
    allowCustomOptions &&
    !isManualInput &&
    inputValue.trim() &&
    !options?.find((option) => option.label === inputValue)

  const handleOptionClick = (option: Option<T>) => {
    if (!selected) {
      setSelected([option])
      onChange?.([option.value])
    } else {
      if (selected.find((opt) => opt.value === option.value)) {
        const values = selected.filter((opt) => opt.value !== option.value)
        setSelected(values)
        onChange?.(values.map((opt) => opt.value))
      } else {
        setSelected([...selected, option])
        onChange?.([...selected, option].map((opt) => opt.value))
      }
    }

    searchRef.current?.focus()
    // reset search field
    setInputValue('')
    setAvailableOptions(options ?? [])
  }

  const handleAddNewOption = (value: string) => {
    if (!options) return

    const newOption: CustomOption<T> = { label: value, value: value as T, custom: true }
    setCustomOptions((customOptions) => [...customOptions, newOption])
    onChange?.([...(selected ?? []), newOption].map((opt) => opt.value))
    setSelected((selected) => [...(selected ?? []), newOption])
    setInputValue('')
  }

  const handleSearch = (text: string) => {
    if (!searchable || !options) return
    setInputValue(text)
    onTextChange?.(text)

    // a pasted value often carries surrounding spaces or line breaks, which would match nothing
    const searchText = text.trim().toLowerCase()
    const filteredOptions = [...options, ...customOptions]?.filter((option) =>
      option.label?.toLowerCase().includes(searchText)
    )

    setAvailableOptions(filteredOptions)
  }

  useEffect(() => {
    if (options) setAvailableOptions(options)
    if (customOptions) setAvailableOptions([...(options ?? []), ...customOptions])
  }, [options, customOptions])

  useEffect(() => {
    setSelected(selectedOptions ?? [])
  }, [selectedOptions])

  useOnClickOutside(ref, () => {
    if (isDisabled) return
    setIsOpen(false)
    if (isOpen) onBlur?.()
  })

  useEffect(() => {
    if (!isOpen) setInputValue('')
  }, [isOpen])

  useEffect(() => {
    if (dropdownRef.current && highlightedIndex !== -1) {
      const highlightedElement = dropdownRef.current.children[highlightedIndex] as HTMLElement
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: 'nearest' })
      }
    }
  }, [highlightedIndex])

  // Scroll to first selected option when dropdown opens
  useEffect(() => {
    if (!isOpen || !dropdownRef.current || !selected || selected.length === 0) return

    const firstSelectedOption = selected[0]
    const optionIndex = availableOptions.findIndex(
      (opt) => opt.value === firstSelectedOption.value
    )

    if (optionIndex !== -1) {
      const customOptionOffset = showCustomOption ? 1 : 0
      const elementIndex = optionIndex + customOptionOffset
      const element = dropdownRef.current.children[elementIndex] as HTMLElement

      if (element) dropdownRef.current?.scrollTo({ top: element.offsetTop - 10 })
    }
  }, [isOpen])

  const handleClickOutside = () => {
    if (!isOpen) return

    setIsOpen(false)
    setHighlightedIndex(-1)

    const textValue = (options ?? []).find(
      (option) => option.label?.toLowerCase() === inputValue?.trim().toLowerCase()
    )
    if (textValue) {
      handleOptionClick(textValue)
    } else if (allowNewOption && inputValue) {
      handleAddNewOption(inputValue)
    }
    setInputValue('')
    onBlur?.()
  }

  useKeydown(() => {
    if (isDisabled) return
    setIsOpen(false)
    handleClickOutside()
  }, ['Tab'])

  useEffect(() => {
    if (!isDisabled) return

    setIsOpen(false)
    setHighlightedIndex(-1)
    searchRef.current?.blur()
  }, [isDisabled])

  const hasNewOption: boolean | undefined =
    inputValue && allowNewOption && !availableOptions.find((option) => option.label === inputValue)
      ? true
      : undefined

  return (
    <div
      ref={ref}
      className={cn('relative w-full outline-none', {
        'pointer-events-none': isDisabled
      })}
      onKeyDown={(e) => {
        if (isDisabled) return

        handleKeyDown<T>(e, {
          isOpen,
          setIsOpen,
          setHighlightedIndex,
          highlightedIndex,
          availableOptions,
          inputValue,
          hasNewOption,
          handleAddNewOption: (input) => handleAddNewOption(input),
          handleOptionClick: (option) => handleOptionClick(option),
          closeOnSelect: false,
          onBlur: () => {
            setIsOpen(false)
            onBlur?.()
          }
        })
      }}
      onFocus={() => {
        if (isDisabled) return
        setIsOpen(true)
      }}
      tabIndex={isDisabled || searchable || isHovered ? undefined : 0}
    >
      <InputWrapper
        {...props}
        active={isOpen || (selected != null && selected?.length > 0)}
        focused={isOpen}
        onClick={() => {
          if (props.disabled || props.readOnly || isManualInput) return

          if (searchable) {
            const searchElement = searchRef.current
            if (!searchElement) return

            if (isOpen) {
              toggle()
              searchElement.blur()
              return
            }

            searchElement.focus()
            return
          }

          toggle()
        }}
      >
        <div
          className={cn(
            'flex items-center justify-end flex-wrap',
            options !== undefined && 'cursor-pointer'
          )}
          onClick={(e) => {
            if (props.disabled || isManualInput || searchable || props.readOnly) return
            e.stopPropagation()
            toggle()
          }}
        >
          <div className="flex w-fit items-center gap-1 flex-wrap mr-auto">
            {selected?.map((option, index) => (
              <div
                key={index}
                className={cn(
                  'bg-primary-light px-2 py-1 rounded-sm flex items-center gap-1 select-none',
                  { grayscale: props.disabled }
                )}
                style={{
                  backgroundColor: colorsMapping[option.variant ?? variant].background
                }}
              >
                <Typography
                  variant="label-medium"
                  color={colorsMapping[option.variant ?? variant].text}
                >
                  {option.label || option.value}
                </Typography>
                <div
                  className={cn('hover:bg-primary-light-active rounded-full p-0.5')}
                  style={{
                    backgroundColor: colorsMapping[option.variant ?? variant].background
                  }}
                  onClick={(e) => {
                    e.stopPropagation()
                    handleOptionClick(option)
                  }}
                >
                  <XIcon color={colorsMapping[option.variant ?? variant].text} />
                </div>
              </div>
            ))}
          </div>

          {!isManualInput && searchable && (
            <input
              ref={searchRef}
              type="text"
              value={inputValue}
              style={typography['label-medium']}
              disabled={props.disabled}
              readOnly={props.readOnly}
              className={cn(
                'outline-none flex-1 bg-transparent disabled:bg-transparent disabled:opacity-100',
                {
                  'text-grey-light-active': props.disabled
                }
              )}
              onChange={(e) => handleSearch(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              onFocus={() => {
                if (isDisabled) return
                setIsOpen(true)
                handleSearch(inputValue)
              }}
              onKeyDown={(e) => {
                const value = e.currentTarget.value.trim()
                if (e.key === 'Enter') {
                  if (value) {
                    // The Enter picks the typed option, so it must not also reach the surrounding container
                    e.nativeEvent.stopPropagation()
                    const existingOption = selected?.find((opt) =>
                      opt.label?.toLowerCase().includes(value?.toLowerCase())
                    )
                    if (!existingOption) {
                      const newOption = options?.find(
                        (opt) => opt.label?.toLowerCase() === value?.toLowerCase()
                      )
                      if (newOption) {
                        handleOptionClick(newOption)
                      } else if (allowNewOption) {
                        handleAddNewOption(value)
                      }
                      e.currentTarget.value = ''
                      setInputValue('')
                    }
                  }
                  e.preventDefault()
                }
                if (e.key === 'Backspace') {
                  if (e.currentTarget.value === '' && selected?.length) {
                    const lastOption = selected[selected.length - 1]
                    handleOptionClick(lastOption)
                  }
                }
              }}
            />
          )}
          {isManualInput && !searchable && (
            <>
              <input
                type={type}
                style={typography['label-medium']}
                className="flex-1 outline-none h-4 bg-transparent disabled:bg-transparent disabled:opacity-100"
                value={tempValue}
                disabled={props.disabled}
                readOnly={props.readOnly}
                onChange={(e) => setTempValue(e.target.value)}
                onKeyDown={(e) => {
                  if (isDisabled) return

                  const events = [
                    'Backspace',
                    'Delete',
                    'Tab',
                    'Enter',
                    'ArrowLeft',
                    'ArrowRight',
                    'ArrowUp',
                    'ArrowDown'
                  ]
                  // Allow letters, spaces, accents, and common punctuation for text input
                  if (
                    type === 'text' &&
                    !ENGLISH_ALPHABET_REGEX.test(e.key) &&
                    !events.includes(e.key) &&
                    !e.ctrlKey &&
                    !e.metaKey
                  ) {
                    e.preventDefault()
                    return
                  }
                  if (type === 'number') {
                    if (!allowDecimals && (e.key === '.' || e.key === ',')) e.preventDefault()
                    if (e.key === 'e' || e.key === 'E' || e.key === '+') e.preventDefault()
                  }
                  if (type === 'email') {
                    const emailRegex = /^[a-zA-Z0-9@.\-_+]$/
                    if (
                      !emailRegex.test(e.key) &&
                      !events.includes(e.key) &&
                      !e.ctrlKey &&
                      !e.metaKey
                    ) {
                      e.preventDefault()
                      return
                    }
                  }
                  if (e.key === 'Tab') {
                    const value = tempValue.trim()
                    if (!value) return
                    if (selected?.find((opt) => opt.value === value)) {
                      setTempValue('')
                      return
                    }
                    handleOptionClick({ label: value, value: value as T })
                    setTempValue('')
                  }
                  if (e.key === 'Enter') {
                    // Prevent form submission/reload when pressing Enter inside a form
                    e.preventDefault()
                    const value = tempValue.trim()
                    if (!value) return
                    // The Enter adds the typed value, so it must not also reach the surrounding container
                    e.nativeEvent.stopPropagation()
                    if (selected?.find((opt) => opt.value === value)) {
                      setTempValue('')
                      return
                    }
                    handleOptionClick({ label: value, value: value as T })
                    setTempValue('')
                  }
                  if (e.key === 'Backspace') {
                    if (selected?.length === 0 || tempValue) return
                    const values = selected?.slice(0, -1) ?? []
                    setSelected(values)
                    onChange?.(values.map((opt) => opt.value))
                  }
                }}
                onBlur={() => {
                  if (isDisabled) return

                  const value = tempValue.trim()
                  if (!value) return
                  if (selected?.find((opt) => opt.value === value)) {
                    setTempValue('')
                    return
                  }
                  handleOptionClick({ label: value, value: value as T })
                  setTempValue('')
                }}
              />
            </>
          )}
          {!isManualInput && (
            <ChevronDownIcon
              className={cn('transition-transform duration-200', {
                'transform rotate-180': isOpen
              })}
              color={
                props.disabled ? tailwindColors.grey.light.active : tailwindColors.grey.DEFAULT
              }
              width="18px"
              height="18px"
            />
          )}
        </div>
      </InputWrapper>

      {!isManualInput && (
        <div
          ref={dropdownRef}
          tabIndex={99999}
          className={cn(
            'absolute z-20 w-full bg-white shadow rounded mt-1 transition-all py-2 duration-150 ease-out transform origin-top max-h-48 overflow-y-auto',
            {
              'scale-y-100': isOpen,
              'scale-y-0 opacity-0 pointer-events-none': !isOpen,
              'fixed z-50': dropDownPosition === 'above'
            }
          )}
          style={{
            width: dropDownPosition === 'above' ? dropdownDimensions?.width : '100%',
            top: dropDownPosition === 'above' ? dropdownDimensions?.top : '100%',
            transition: 'opacity 0.15s ease-out, transform 0.15s ease-out'
          }}
        >
          {showCustomOption && (
            <div
              className={cn(
                'flex items-center justify-between gap-2 w-full px-4 py-2 text-left transition-colors',
                highlightedIndex === 0 ? 'bg-white-active' : 'hover:bg-white-active'
              )}
              onClick={() => handleAddNewOption(inputValue)}
            >
              <Typography variant="label-semibold">
                {t('common.select.add.option', { value: inputValue })}
              </Typography>
            </div>
          )}
          {availableOptions.map((option, index) => (
            <div
              key={index}
              className={cn(
                'py-2 px-3 flex items-center space-x-2 w-full transition-colors',
                highlightedIndex === (hasNewOption ? index + 1 : index)
                  ? 'bg-white-active'
                  : 'hover:bg-white-active'
              )}
            >
              <Checkbox
                wrapperClassName="flex-1 cursor-pointer"
                checked={selected?.some((opt) => opt.value === option.value)}
                onChange={() => handleOptionClick(option)}
                label={option.label}
                component={option.component}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default MultiSelectInput
